import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { TradeParties } from "./terms.js";
/** Shipments, tracking, and proof of delivery (LOG-1, LOG-2, card 057). */
export const ShipmentStatus = z.enum(["in_transit", "delivered", "exception"]).meta({ id: "ShipmentStatus" });
export const ShipmentDocumentKind = z
    .enum(["packing_list", "transport_document", "commercial_invoice", "other"])
    .meta({ id: "ShipmentDocumentKind" });
export const TrackingEventView = z
    .object({ occurredAt: Timestamp, status: z.string(), location: z.string().nullable() })
    .meta({ id: "TrackingEvent" });
export const ShipmentView = z
    .object({
    id: Id,
    orderId: Id,
    status: ShipmentStatus,
    carrier: z.string(),
    trackingNumber: z.string(),
    tracked: z.boolean().describe("Whether the tracking service took the shipment"),
    shippedAt: Timestamp,
    deliveredAt: Timestamp.nullable(),
    deliverySource: z.enum(["tracking", "proof"]).nullable(),
    proofOfDeliveryFileId: Id.nullable(),
    lines: z.array(z.object({
        orderLineId: Id,
        productName: LocalizedText,
        skuCode: z.string().nullable(),
        quantity: Quantity,
        unit: z.string(),
    })),
    documents: z.array(z.object({ id: Id, fileId: Id, kind: ShipmentDocumentKind })),
    events: z.array(TrackingEventView).describe("Newest first"),
    version: z.number().int().positive().describe("Sent as `If-Match` to correct it"),
    actions: z.array(z.enum(["correct", "proof_of_delivery"])).describe("What the caller may do now"),
})
    .meta({ id: "Shipment" });
/** A shipment as its card shows it in the deal conversation, read afresh for each viewer. */
export const ShipmentCardData = z
    .object({
    orderNumber: z.string(),
    ...TradeParties,
    status: ShipmentStatus,
    carrier: z.string(),
    trackingNumber: z.string(),
    shippedAt: Timestamp,
    deliveredAt: Timestamp.nullable(),
    lines: z.array(ShipmentView.shape.lines.element.pick({ productName: true, quantity: true, unit: true })),
    lastEvent: TrackingEventView.nullable(),
    confirmationDueAt: Timestamp.nullable().describe("When the buyer's window to confirm receipt ends"),
})
    .meta({ id: "ShipmentCardData" });
const Party = { surface: "tenant", auth: "user" };
const ShipmentId = z.object({ shipmentId: Id });
export const recordShipment = defineRoute({
    ...Party,
    permission: "shipment:manage",
    id: "trade.shipments.create",
    method: "POST",
    path: "/v1/orders/{orderId}/shipments",
    marks: ["tenant", "idem"],
    summary: "The shop records a shipment of a paid order: lines and quantities within what is still open, carrier, tracking number, when it left, and documents uploaded for the order. Tracking is registered; when every line is shipped, a balance due at shipment becomes due",
    prd: ["LOG-1"],
    request: {
        params: z.object({ orderId: Id }),
        body: z.object({
            lines: z
                .array(z.object({ orderLineId: Id, quantity: Quantity }))
                .min(1)
                .max(50),
            carrier: z.string().trim().min(1).max(60),
            trackingNumber: z.string().trim().min(1).max(60),
            shippedAt: Timestamp,
            documents: z
                .array(z.object({ fileId: Id, kind: ShipmentDocumentKind }))
                .max(10)
                .default([])
                .describe("Clean `shipment_document` files uploaded for the order"),
        }),
    },
    responses: { 201: { description: "The shipment", schema: ShipmentView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "EXCEEDS_OPEN_QUANTITY",
    ],
});
export const listShipments = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.shipments.list",
    method: "GET",
    path: "/v1/orders/{orderId}/shipments",
    marks: ["tenant"],
    summary: "The order's shipments, oldest first, with their tracking events",
    prd: ["LOG-2"],
    request: { params: z.object({ orderId: Id }) },
    responses: { 200: { description: "Shipments", schema: z.object({ items: z.array(ShipmentView) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getShipment = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.shipments.get",
    method: "GET",
    path: "/v1/shipments/{shipmentId}",
    marks: ["tenant"],
    summary: "One shipment with its tracking events",
    prd: ["LOG-2"],
    request: { params: ShipmentId },
    responses: { 200: { description: "The shipment", schema: ShipmentView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const correctShipment = defineRoute({
    ...Party,
    permission: "shipment:manage",
    id: "trade.shipments.correct",
    method: "PATCH",
    path: "/v1/shipments/{shipmentId}",
    marks: ["tenant", "if-match"],
    summary: "The shop corrects the carrier or tracking number before delivery; tracking is registered again",
    prd: ["LOG-1"],
    request: {
        params: ShipmentId,
        body: z.object({
            carrier: z.string().trim().min(1).max(60).optional(),
            trackingNumber: z.string().trim().min(1).max(60).optional(),
        }),
    },
    responses: { 200: { description: "The shipment", schema: ShipmentView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "VERSION_CONFLICT",
    ],
});
export const proveDelivery = defineRoute({
    ...Party,
    permission: "shipment:manage",
    id: "trade.shipments.proof_of_delivery",
    method: "POST",
    path: "/v1/shipments/{shipmentId}/proof-of-delivery",
    marks: ["tenant", "idem"],
    summary: "When tracking gives no delivered event, the shop attaches a clean `proof_of_delivery` uploaded for the order, with when it was delivered; the shipment is delivered, and once all are, the buyer's window to confirm receipt starts",
    prd: ["LOG-2"],
    request: {
        params: ShipmentId,
        body: z.object({
            fileId: Id,
            deliveredAt: Timestamp.optional().describe("Now when absent; not before it was shipped, nor in the future"),
        }),
    },
    responses: { 200: { description: "The shipment", schema: ShipmentView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const shipmentRoutes = [
    recordShipment,
    listShipments,
    getShipment,
    correctShipment,
    proveDelivery,
];
//# sourceMappingURL=shipment.routes.js.map